import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    InputAdornment,
    List,
    ListItemButton,
    ListItemText,
    TextField,
    Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { api, http, saveBlob } from '../../services/Server';
import { formatApiError } from '../../utils/Error';
import { displayName } from './helpers';
import type { AnyEntity } from './types';

interface Props {
  open: boolean;
  onClose: () => void;
}

const SUPPLIER_PATH = 'suppliers';

// Latest-updated first; suppliers without a timestamp fall to the bottom.
const compareByUpdatedDesc = (a: AnyEntity, b: AnyEntity): number => {
  const ta = a.dt_updated ? new Date(a.dt_updated as string).getTime() : 0;
  const tb = b.dt_updated ? new Date(b.dt_updated as string).getTime() : 0;
  return tb - ta;
};

const formatTimestamp = (value: unknown): string => {
  if (!value) return '-';
  const d = new Date(value as string);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
};

const ExportSuppliersDialog = ({ open, onClose }: Props) => {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [search, setSearch] = useState('');
  const [exporting, setExporting] = useState(false);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['list', SUPPLIER_PATH],
    queryFn: () => api.list<AnyEntity>(SUPPLIER_PATH),
    enabled: open,
  });

  const sorted = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    const list = q
      ? data.filter((d) => String(d.name ?? '').toLowerCase().includes(q))
      : data;
    return list.slice().sort(compareByUpdatedDesc);
  }, [data, search]);

  const allSelected = sorted.length > 0 && sorted.every((s) => selectedIds.has(s.id));
  const someSelected = selectedIds.size > 0 && !allSelected;

  const toggleOne = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(sorted.map((s) => s.id)));
    }
  };

  const handleClose = () => {
    if (exporting) return;
    setSelectedIds(new Set());
    setSearch('');
    onClose();
  };

  const handleExport = async () => {
    if (selectedIds.size === 0) return;
    setExporting(true);
    try {
      const ids = Array.from(selectedIds);
      const params = new URLSearchParams();
      ids.forEach((id) => params.append('ids', String(id)));

      const { data } = await http.get<Blob>(`${SUPPLIER_PATH}/export`, {
        params,
        responseType: 'blob',
      });

      const stamp = new Date().toISOString().slice(0, 10);
      saveBlob(data, `suppliers_${stamp}.xlsx`);

      toast.success(`Exported ${ids.length} supplier${ids.length === 1 ? '' : 's'}`);
      handleClose();
    } catch (e) {
      toast.error(formatApiError(e, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: '#002A5C' }}>Export suppliers</DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          <TextField
            size="small"
            placeholder="Search suppliers"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: '#8E99A8' }} />
                  </InputAdornment>
                ),
              },
            }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={toggleAll}
                  disabled={sorted.length === 0}
                />
              }
              label={
                <Typography variant="caption" sx={{ color: '#5C6B7A', fontWeight: 600 }}>
                  Select all ({sorted.length})
                </Typography>
              }
            />
            <Typography variant="caption" sx={{ color: '#5C6B7A', fontWeight: 600 }}>
              {selectedIds.size} selected
            </Typography>
          </Box>
        </Box>
        <Divider />
        <Box sx={{ maxHeight: 380, overflow: 'auto' }}>
          {isLoading && (
            <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={20} />
            </Box>
          )}
          {isError && (
            <Box sx={{ p: 2 }}>
              <Typography color="error" variant="body2">
                {(error as Error)?.message ?? 'Failed to load suppliers'}
              </Typography>
            </Box>
          )}
          {!isLoading && !isError && (
            <List dense disablePadding>
              {sorted.map((item) => {
                const checked = selectedIds.has(item.id);
                return (
                  <ListItemButton key={item.id} onClick={() => toggleOne(item.id)} dense>
                    <Checkbox
                      edge="start"
                      size="small"
                      checked={checked}
                      tabIndex={-1}
                      disableRipple
                    />
                    <ListItemText
                      primary={
                        <Typography sx={{ fontWeight: 600, fontSize: '0.88rem', color: '#002A5C' }}>
                          {displayName(item, 'Supplier')}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" sx={{ color: '#8E99A8' }}>
                          Updated {formatTimestamp(item.dt_updated)}
                        </Typography>
                      }
                    />
                  </ListItemButton>
                );
              })}
              {sorted.length === 0 && (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ color: '#5C6B7A' }}>
                    No suppliers found
                  </Typography>
                </Box>
              )}
            </List>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={exporting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
          onClick={handleExport}
          disabled={exporting || selectedIds.size === 0}
        >
          Export ({selectedIds.size})
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExportSuppliersDialog;
